import React, { useState, useEffect } from "react";
import {
	initialFilter,
	ProgressChartFilter,
	ProgressChartFilters,
} from "../../shared/progress/ProgressChartFilter";
import { DraftType } from "../../shared/progress/DraftType";
import { ProgressChartData, MonthlyScoresChart } from "../../shared/progress/MonthlyScoresChart";
import { useLazyGetMonthlyAverageScoresQuery } from "../../shared/progress/progressReportApiSlice";
import {
	MonthlyScoresFilteredByCourseViewModel,
	MonthlyScoreViewModel,
} from "./MonthlyScoresFilteredByCourseViewModel";

export function MonthlyScoresChartWithFilters(props: {
	className: string;
	courseId: number;
	data: MonthlyScoresFilteredByCourseViewModel;
}) {
	const [filter, setFilter] = useState<ProgressChartFilter>(initialFilter);
	const [scores, setScores] = useState<MonthlyScoreViewModel[]>(props.data.scores);
	const [chartData, setChartData] = useState<ProgressChartData[]>([]);

	const [getScores] = useLazyGetMonthlyAverageScoresQuery();

	const handleFilterChange = (_filter: ProgressChartFilter) => {
		if (_filter.genre != filter.genre || _filter.trait != filter.trait) {
			const beginSchoolYear = props.data.schoolYears[0];
			const endSchoolYear = props.data.schoolYears[props.data.schoolYears.length - 1];

			getScores(
				{
					courseId: props.courseId,
					beginSchoolYear,
					endSchoolYear,
					scoreCategoryId: _filter.trait,
					genreId: _filter.genre,
				},
				true // cache the results
			)
				.unwrap()
				.then((data) => {
					setScores(data);
				});
		}
		setFilter(_filter);
	};

	useEffect(() => {
		const data = filterChartData(filter.year, filter.type, scores, props.data);
		setChartData(data);
	}, [props.data, scores, filter.year, filter.type]);

	return (
		<div className="mt-8 p-4 border-2 border-gray-200">
			<ProgressChartFilters
				filter={filter}
				schoolYears={props.data.schoolYearList}
				draftTypes={props.data.draftTypes}
				onChange={handleFilterChange}
			/>

			<MonthlyScoresChart className="mt-8 p-4" filter={filter} chartData={chartData} />
		</div>
	);
}

function filterChartData(
	year: number,
	draftType: number,
	scores: MonthlyScoreViewModel[],
	reportData: MonthlyScoresFilteredByCourseViewModel
): ProgressChartData[] {
	const years = year === 0 ? reportData.schoolYears : [year];
	const numberOfSchoolMonthsBeforeEndOfYear = 12 - reportData.schoolYearStartMonth;
	const firstDraftsName =
		reportData.draftTypes.find((dt) => +dt.value == DraftType.First)?.text ?? "First Drafts";
	const lastDraftsName =
		reportData.draftTypes.find((dt) => +dt.value == DraftType.Last)?.text ?? "Last Drafts";

	const chartData: ProgressChartData[] = [];

	years.forEach((_year) => {
		const lastDrafts: (number | undefined)[] = [];
		const firstDrafts: (number | undefined)[] = [];

		scores
			.filter((score) => score.schoolYear == _year)
			.forEach((score) => {
				if (score.lastDraftScore != null) {
					lastDrafts[(score.month + numberOfSchoolMonthsBeforeEndOfYear) % 12] =
						score.lastDraftScore;
				}
				if (score.firstDraftScore != null) {
					firstDrafts[(score.month + numberOfSchoolMonthsBeforeEndOfYear) % 12] =
						score.firstDraftScore;
				}
			});

		if (
			firstDrafts.length != 0 &&
			(draftType === DraftType.First || draftType === DraftType.Both)
		) {
			chartData.push({
				year: _year,
				draftType: DraftType.First,
				typeName: firstDraftsName,
				scores: firstDrafts,
			});
		}
		if (
			lastDrafts.length != 0 &&
			(draftType === DraftType.Last || draftType === DraftType.Both)
		) {
			chartData.push({
				year: _year,
				draftType: DraftType.Last,
				typeName: lastDraftsName,
				scores: lastDrafts,
			});
		}
	});

	return chartData;
}
